import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ProfileContext } from "../contexts/ProfileContext";
import { LoadingSpinner } from "../components/loading-spinner";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import OrganizationNameButton from "../components/organization-name-button";
import "../styles/Dashboard-new.css";

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const PrayerRequests = () => {
  // ========== COMMON STATE ==========
  const [dataType, setDataType] = useState("ytd");
  const [exportOption, setExportOption] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [selectedRange, setSelectedRange] = useState({ start: "", end: "" });
  const [isLoading, setIsLoading] = useState(false);

  // ========== PAGE-SPECIFIC STATE ==========
  const [donutData, setDonutData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(true);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [totalPrayerRequest, setTotalPrayerRequest] = useState("");

  // ========== REFS ==========
  const selectRef = useRef(null);
  const datePickerRef = useRef(null);
  const reportRef = useRef();

  // ========== CONTEXT ==========
  const { getPrayerRequests, organizationNumber, organization, worker } =
    useContext(ProfileContext);
  const orgNumber = organizationNumber;

  // ========== DATE HELPERS ==========
  const formatDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  const getYTDRange = () => {
    const now = new Date();
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
    return { start: formatDate(firstDayOfYear), end: formatDate(now) };
  };

  const getDefaultMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(firstDay), end: formatDate(now) };
  };

  // ========== DATA FETCHING ==========
  const fetchPrayerData = async (type = "", startDate = "", endDate = "") => {
    try {
      setIsLoading(true);
      setIsLoadingSummary(true);

      let result;

      if (type === "all") {
        const finalStart = "2015-01-01";
        const finalEnd = formatDate(new Date());
        result = await getPrayerRequests(orgNumber, finalStart, finalEnd);
        setSelectedRange({ start: finalStart, end: finalEnd });
      } else if (type === "ytd") {
        const ytd = getYTDRange();
        result = await getPrayerRequests(orgNumber, ytd.start, ytd.end);
        setSelectedRange(ytd);
      } else if (type === "monthly") {
        const def = getDefaultMonthRange();
        result = await getPrayerRequests(orgNumber, def.start, def.end);
        setSelectedRange(def);
      } else if (type === "custom") {
        if (!startDate || !endDate) {
          console.warn("Custom date range selected but no dates provided");
          return;
        }
        result = await getPrayerRequests(orgNumber, startDate, endDate);
        setSelectedRange({ start: startDate, end: endDate });
      } else {
        // Default case - fetch without dates
        result = await getPrayerRequests(orgNumber);
        setSelectedRange({ start: "", end: "" });
      }

      if (!result) {
        setDonutData([]);
        setLineData([]);
        return;
      }

      const categoryColors = [
        "#EC3310",
        "#106271",
        "#64868D",
        "#DE735E",
        "#00323B",
        "#FFB0A2",
        "#FCD5C9",
        "#D9EEF0",
        "#2DB3C0",
        "#E29878",
        "#ADCFD4",
        "#DDC2B7",
        "#77B3B8",
        "#FFB0A2",
        "#FF7234",
        "#BFE9EE",
        "#178B96",
        "#2DB3C0",
        "#FFEAE4",
        "#77B3B8",
        "#8DDCE8",
        "#FFB0A2",
        "#E8B1A7",
        "#00323B",
        "#E4572E",
        "#76B041",
        "#F2A104",
        "#6699CC",
        "#D7263D",
        "#3B8EA5",
        "#F45D01",
        "#2E8B57",
        "#F28E2B",
        "#4E79A7",
        "#59A14F",
        "#E15759",
        "#B07AA1",
        "#9C755F",
        "#76B7B2",
        "#FF9F1C",
        "#2EC4B6",
        "#E71D36",
        "#3A86FF",
        "#8338EC",
        "#FB5607",
        "#FF006E",
        "#FFD166",
        "#06D6A0",
        "#118AB2",
        "#EF476F",
        "#073B4C",
        "#8AC926",
        "#FFCA3A",
        "#1982C4",
      ];

      const apiData = result ?? {};
      const totalPrayerRequestCount = apiData["total_prayer_requests"] ?? 0;
      const categoriesCount = apiData["categories_count"] ?? {};

      const categoryReport = Object.keys(categoriesCount).map(
        (category, index) => ({
          name: category,
          value: categoriesCount[category],
          color: categoryColors[index % categoryColors.length] || "#7F7F7F",
        })
      );

      // Process age range data for line chart
      const ageRangeCount = apiData.age_range_count || {};
      const ageRangeData = Object.keys(ageRangeCount).map((ageRange) => ({
        name: ageRange,
        value: ageRangeCount[ageRange],
      }));

      setTotalPrayerRequest(totalPrayerRequestCount);
      setDonutData(categoryReport);
      setLineData(ageRangeData);
    } catch (error) {
      console.error("❌ Error fetching prayer requests report:", error);
      setDonutData([]);
      setLineData([]);
    } finally {
      setIsLoading(false);
      setIsLoadingSummary(false);
    }
  };

  // ========== INITIAL FETCH ==========
  useEffect(() => {
    fetchPrayerData("ytd");
  }, []);

  // ========== FILTER HANDLERS ==========
  const handleFilterChange = (type) => {
    setDataType(type);
    if (type === "custom") {
      setShowDatePicker(true);
    } else {
      fetchPrayerData(type);
    }
  };

  const handleApplyCustomDates = () => {
    if (!tempStartDate || !tempEndDate) {
      alert("Select both dates.");
      return;
    }
    setDataType("custom");
    setShowDatePicker(false);
    fetchPrayerData("custom", tempStartDate, tempEndDate);
  };

  const handleCancelCustomDates = () => {
    setTempStartDate("");
    setTempEndDate("");
    setShowDatePicker(false);
  };

  // ========== EXPORT FUNCTIONALITY ==========
  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: `Prayer_Requests_Report_${selectedRange.start || "all"}_to_${
      selectedRange.end || "all"
    }`,
    onBeforePrint: () => {
      document.body.style.width = "100%";
    },
    onAfterPrint: () => {
      setExportOption("");
      document.body.style.width = "";
    },
  });

  const handleExportExcel = () => {
    const categorySheetData = donutData.length
      ? donutData.map((d) => ({
          Category: "Prayer Category",
          Label: d.name,
          Count: d.value,
        }))
      : [{ Category: "Prayer Category", Label: "No Data", Count: 0 }];

    const ageRangeSheetData = lineData.length
      ? lineData.map((d) => ({
          Category: "Age Range",
          Label: d.name,
          Count: d.value,
        }))
      : [{ Category: "Age Range", Label: "No Data", Count: 0 }];

    const categoryTotal = donutData.reduce((sum, item) => sum + item.value, 0);
    const ageRangeTotal = lineData.reduce((sum, item) => sum + item.value, 0);

    categorySheetData.push({
      Category: "Prayer Category",
      Label: "TOTAL",
      Count: categoryTotal,
    });
    ageRangeSheetData.push({
      Category: "Age Range",
      Label: "TOTAL",
      Count: ageRangeTotal,
    });

    const worksheet = XLSX.utils.json_to_sheet([
      ...categorySheetData,
      ...ageRangeSheetData,
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Prayer_Requests_Report");

    const fileName = `Prayer_Requests_Report_${
      selectedRange.start || "all"
    }_to_${selectedRange.end || "all"}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    setExportOption("");
  };

  useEffect(() => {
    if (exportOption === "print") handlePrint();
    if (exportOption === "export") handleExportExcel();
  }, [exportOption]);

  // ========== UI HANDLERS ==========
  const toggleBreakdown = () => {
    setIsBreakdownOpen(!isBreakdownOpen);
  };

  // ========== SELECT & DATEPICKER EFFECTS ==========
  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);

    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);

    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  // ========== CALCULATIONS ==========
  const categoryTotal = donutData.reduce((sum, item) => sum + item.value, 0);
  const ageRangeTotal = lineData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Wrapper>
      {/* ========== STANDARDIZED TOP SECTION ========== */}
      <TopSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? null : (
          <OrganizationNameButton />
        )}

        <TitleText>Prayer Requests</TitleText>
      </TopSection>

      {/* ========== STANDARDIZED TOGGLE FILTERS ========== */}
      <ToggleButtonContainer>
        <ToggleContent>
          <ToggleButtonGroup>
            <ToggleButton
              active={dataType === "ytd"}
              onClick={() => handleFilterChange("ytd")}
            >
              YEAR TO DATE
            </ToggleButton>

            <ToggleButton
              active={dataType === "all"}
              onClick={() => handleFilterChange("all")}
            >
              ALL DATA
            </ToggleButton>

            <ToggleButton
              active={dataType === "monthly"}
              onClick={() => handleFilterChange("monthly")}
            >
              CURRENT MONTH
            </ToggleButton>

            <ToggleButton
              active={dataType === "custom"}
              onClick={() => {
                setDataType("custom");
                setShowDatePicker(true);
              }}
            >
              CUSTOM DATE RANGE
            </ToggleButton>
          </ToggleButtonGroup>

          <ExportContainer>
            <SelectWrapper>
              <ExportSelect
                ref={selectRef}
                value={exportOption}
                onChange={(e) => setExportOption(e.target.value)}
              >
                <option value="" disabled>
                  Export Options
                </option>
                <option value="print">Print PDF</option>
                <option value="export">Export as Excel Sheet</option>
              </ExportSelect>
              <ArrowIcon isOpen={isSelectOpen}>
                <ChevronDown />
              </ArrowIcon>
            </SelectWrapper>
          </ExportContainer>
        </ToggleContent>

        {showDatePicker && (
          <DatePickerDropdown ref={datePickerRef}>
            <DateInputGroup>
              <label>From</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </DateInputGroup>

            <DateInputGroup>
              <label>To</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </DateInputGroup>

            <DatePickerActions>
              <button onClick={handleApplyCustomDates}>Apply</button>
              <button onClick={handleCancelCustomDates}>Cancel</button>
            </DatePickerActions>
          </DatePickerDropdown>
        )}
      </ToggleButtonContainer>

      <PrintContainer ref={reportRef}>
        {/* ========== HIDDEN PDF-ONLY TITLE ========== */}
        <PdfOnlyHeader>
          <div className="main-title">PRAYER REQUESTS</div>
        </PdfOnlyHeader>

        {/* ========== PAGE-SPECIFIC CONTENT ========== */}
        <div className="stats-grid-prayer">
          <div className="stat-card-new stat-white-new stat-border">
            <div className="stat-content-new stat-white-new">
              {isLoadingSummary ? (
                <>
                  <div className="skeleton skeleton-number"></div>
                  <div className="skeleton skeleton-label"></div>
                </>
              ) : (
                <>
                  <div className="stat-number-new">
                    {totalPrayerRequest.toLocaleString("en-US")}
                  </div>
                  <p className="stat-label-bottom teal">PRAYERLINE - PRAYER REQUESTS</p>
                </>
              )}
            </div>
          </div>
        </div>
        <ChartsContainer>
          <DonutCard>
            {isLoading ? (
              <SpinnerWrapper>
                <LoadingSpinner />
              </SpinnerWrapper>
            ) : (
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={
                        donutData.length
                          ? donutData
                          : [{ name: "No Data", value: 1, color: "#E0E0E0" }]
                      }
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="90%"
                      paddingAngle={0}
                      dataKey="value"
                    >
                      {(donutData.length
                        ? donutData
                        : [{ color: "#E0E0E0" }]
                      ).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    {donutData.length > 0 && <Tooltip />}
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </DonutCard>

          <LineCard>
            {isLoading ? (
              <SpinnerWrapper>
                <LoadingSpinner />
              </SpinnerWrapper>
            ) : (
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={lineData}
                    margin={{ top: 20, right: 10, left: -20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="name"
                      angle={-20}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 11 }}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#9A89FF"
                      strokeWidth={2}
                      dot={{ fill: "#B1A5FF", r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </LineCard>
        </ChartsContainer>

        <BreakdownCard isOpen={isBreakdownOpen}>
          <BreakdownTitle onClick={toggleBreakdown}>
            <ChevronIcon>
              {isBreakdownOpen ? <ChevronDown /> : <ChevronRight />}
            </ChevronIcon>
            PRAYER REQUESTS BREAKDOWN
          </BreakdownTitle>

          {isBreakdownOpen && (
            <BreakdownContent>
              <BreakdownTables>
                <TableContainer>
                  <TableTitle>Prayer Categories</TableTitle>
                  {isLoading ? (
                    <SpinnerWrapper>
                      <LoadingSpinner />
                    </SpinnerWrapper>
                  ) : (
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>Color</th>
                          <th>Category</th>
                          <th>Total Requests</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donutData.length ? (
                          <>
                            {donutData.map((item, index) => (
                              <tr key={index}>
                                <td>
                                  <TableColorBox color={item.color} />
                                </td>
                                <td className="text-capitalize">{item.name}</td>
                                <td>{item.value}</td>
                              </tr>
                            ))}
                            <tr className="total-row">
                              <td
                                colSpan="2"
                                style={{
                                  textAlign: "left",
                                  fontWeight: "bold",
                                }}
                              >
                                Total
                              </td>
                              <td style={{ fontWeight: "bold" }}>
                                {categoryTotal}
                              </td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="3">
                              No prayer category data available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </StyledTable>
                  )}
                </TableContainer>
              </BreakdownTables>
            </BreakdownContent>
          )}
        </BreakdownCard>
      </PrintContainer>
    </Wrapper>
  );
};

export default PrayerRequests;

// ========== STYLED COMPONENTS ==========

// Add this new styled component for PDF-only header
const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }
  }
`;

// Standardized components from the example
const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const ToggleButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const ToggleContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 62px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
    max-width: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  width: auto;
  min-width: 164px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center;

  border: none;
  border-radius: 62px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#86CCDA")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 6px rgba(0, 0, 0, 0.15)" : "none"};

  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;

    font-size: 14px; /* Keep typography consistent */
    letter-spacing: 0.15em; /* Keep consistent */
    border-radius: 8px;
    margin: 2px 0;
  }

  @media (max-width: 480px) {
    height: 36px;

    font-size: 14px; /* Maintain required size */
    letter-spacing: 0.25em; /* Maintain required spacing */

    padding: 0 8px;
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 280px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 480px) {
    min-width: 260px;
    padding: 12px;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    font-family: "Space Grotesk";
  }

  input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 13px;
    font-family: "Space Grotesk";
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #2db3c0;
      box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.1);
    }
  }
`;

const DatePickerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;

  button {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: "Space Grotesk";
    font-weight: 600;
    border: none;
    transition: all 0.2s;

    &:first-child {
      background-color: #2db3c0;
      color: white;

      &:hover {
        background-color: #249aa5;
      }
    }

    &:last-child {
      background-color: #f3f4f6;
      color: #666;

      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 172px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 200px;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 100%;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const PrintContainer = styled.div`
  width: 100%;
  display: block;

  @media print {
    width: 100%;
    display: block;

    /* Optional: Add page break control */
    page-break-inside: avoid;

    /* Ensure charts and tables don't get cut off */
    .recharts-wrapper,
    .recharts-surface {
      page-break-inside: avoid;
    }
  }
`;

// Your existing styled components (keep as is)
const Wrapper = styled.div`
  width: 100%;
  padding: 1rem 0;
  background-color: #f3f9fa;
  min-height: 100vh;
  overflow-x: hidden;

  @media (max-width: 480px) {
    padding: 0.5rem 0;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DonutCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  width: 100%;

  @media (max-width: 768px) {
    min-height: 350px;
    padding: 0.5rem;
  }

  @media (max-width: 480px) {
    min-height: 300px;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;

  @media (max-width: 768px) {
    height: 250px;
  }

  @media (max-width: 480px) {
    height: 220px;
  }
`;

const LineCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1rem;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  @media (max-width: 768px) {
    min-height: 350px;
    padding: 0.5rem;
  }

  @media (max-width: 480px) {
    min-height: 300px;
  }
`;

const BreakdownCard = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  padding: ${({ isOpen }) => (isOpen ? "1.5rem" : "1rem 1.5rem")};
  font-family: "Space Grotesk", sans-serif;
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 480px) {
    border-radius: 12px;
    padding: ${({ isOpen }) => (isOpen ? "1rem" : "0.75rem 1rem")};
    margin-top: 1.5rem;
  }
`;

const BreakdownTitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold */
  font-style: normal;
  line-height: 100%;
  color: #2db3c0;
  letter-spacing: 0.25em; /* 25% spacing */
  text-align: center;

  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #249aa5;
  }

  @media (max-width: 480px) {
    font-size: 14px; /* Keeping typography consistent */
    letter-spacing: 0.25em; /* Keeping consistent */
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BreakdownContent = styled.div`
  margin-top: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BreakdownTables = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-width: 280px;
  overflow: hidden;

  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const TableTitle = styled.h5`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.8rem;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 14px;
    text-align: left;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  table-layout: fixed;
  font-family: "Space Grotesk", sans-serif;
  background-color: #fff;

  th,
  td {
    padding: 12px 8px;
    border-bottom: 1px solid #dee2e6;
    word-wrap: break-word;
  }

  thead th {
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f2f6f7;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  .total-row {
    background-color: #f2f6f7;
    font-weight: bold;
  }

  @media (max-width: 480px) {
    font-size: 12px;

    th,
    td {
      padding: 8px 4px;
    }

    thead th {
      font-size: 12px;
    }
  }

  @media (max-width: 360px) {
    font-size: 11px;

    th,
    td {
      padding: 6px 3px;
    }
  }
`;

const TableColorBox = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${({ color }) => color};
  border-radius: 4px;
  border: 1px solid #ddd;

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
  }
`;
